import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Divider, Grid, Stack, TableContainer, Tooltip, Typography, TableSortLabel } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { useEffect, useState } from 'react';
import PaymentRequestIcon from '../../../icons/BlackPaymentRequests.svg';
import { getFetchingOwnersData, selectOwners, selectOwnersCurrentPage, selectOwnersState } from '../sagas/ownersSlice';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../../icons/Badrequesterror.svg';
import Error500 from '../../../icons/505Error.svg';
import { DisburseSearchField, OwnerV2Dto, Role, OwnerSortColumnEnum, OwnerSortDirectionEnum, OwnerSort } from '@inosio/payments-sdk';
import { getDisbursementHistoryDetails } from '../../ownerDistributions/sagas/pmDisbursementsSlice';
import ReviewOtpIcon from '../../../icons/ReviewOtpIcon.svg';
import ResidentPortalIcon from '../../../icons/ResidentPortal.svg';
import RegisteredIcon from '../../../icons/RegisteredIcon.svg';
import NotRegisteredIcon from '../../../icons/NotRegisteredIcon.svg';
import { useHistory } from 'react-router';
import { selectOnboardingApplicationState } from '../../onboarding/sagas/pmonboardingSlice';
import UserRegistrationModalManager, { UserRegistrationProfileDetails } from '../../../shared/userRegistration/UserRegistrationModalManager';
import LogAsProfileUserModal from '../../../shared/logas/LogAsProfileUsersModal';
import OwnerCreatePaymentRequestModal from '../../paymentrequests/components/OwnerCreatePaymentRequestModal';
import { getCloseAllModals, getResetPrState } from '../../paymentrequests/sagas/paymentrequestslice';
import { resetQuickPayState } from '../../../resui/quickpayments/sagas/quickpaymentsSlice';
import OwnersFilterTableHeader from './OwnersFilterModal/OwnersFilterTableHeader';

const outlinedButtonStyle = {
  color: '#39A2DB',
  border: '2px solid #39A2DB',
  boxShadow: 'none',
  borderRadius: '24px',
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '14px',
  fontWeight: 700,
  fontStyle: 'normal',
  textTransform: 'capitalize',
  '&:hover': {
    backgroundColor: '#39A2DB',
    color: 'white',
    border: '2px solid #39A2DB',
  },
  width: '200px',
  height: '50px',
};

export default function OwnersDirectory() {
  const dispatch = useDispatch();
  const history = useHistory();

  //selectors
  const { isFetchingOwners, isFetchingOwnersFailed, errorMessage } = useSelector(selectOwnersState);
  let OwnersDataBook = useSelector(selectOwnersCurrentPage);
  const { pmDetails, isFetchingPmDetailsSuccess } = useSelector(selectOnboardingApplicationState);

  //local state
  const [currentPage, setCurrentPage] = useState(0);
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [openLogAsProfileUsersModal, setOpenLogAsProfileUsersModal] = useState(false);
  const [openUserRegistrationModalManager, setOpenUserRegistrationModalManager] = useState(false);
  const [openCreatePaymentRequestModal, setOpenCreatePaymentRequestModal] = useState(false);
  const OwnersData = useSelector(selectOwners);
  const [selectedOwner, setSelectedOwner] = useState<OwnerV2Dto>();
  const isOwnerPortalFeatureEnabled = pmDetails.FeaturesEnabled.includes('OwnerPortal');
  const tableData = isOwnerPortalFeatureEnabled ? OwnersDataBook?.[currentPage]?.Rows : ([] as OwnerV2Dto[]);

  const [selectedSortingColumn, setSelectedSortingColumn] = useState<OwnerSortColumnEnum>(OwnerSortColumnEnum.Name);
  const [selectedSortingDirection, setSelectedSortingDirection] = useState<OwnerSortDirectionEnum>(OwnerSortDirectionEnum.Asc);

  useEffect(() => {
    dispatch(
      getDisbursementHistoryDetails({
        SearchField: DisburseSearchField.Threshold,
        SearchValue: 'ProcessedAmount',
        DisburseType: 'OwnerDistribution', //This is required.
      }),
    );
  }, []);

  useEffect(() => {
    if (selectedOwner?.Uuid) {
      const newItem = OwnersData.Rows.find((item) => item.Uuid === selectedOwner?.Uuid);
      setSelectedOwner(newItem);
    }
  }, [OwnersData, selectedOwner]);

  const setSearchPageIndex = (pageNumber: number) => {
    if (!OwnersDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const handleSearch2 = (rangeSize: number, recordStartIndex: number) => {
    dispatch(
      getFetchingOwnersData({
        RangeSize: rangeSize,
        StartIndex: recordStartIndex,
        Sort: { Column: selectedSortingColumn as OwnerSortColumnEnum, Direction: selectedSortingDirection as OwnerSortDirectionEnum } as OwnerSort,
      }),
    );
  };

  const handleViewOwnerDetails = (dto: OwnerV2Dto) => {
    setSelectedOwner(dto);
    history.push(`/pmui/owners/${dto.Uuid}`);
  };

  const handleOpenModal = (owner: OwnerV2Dto) => {
    setSelectedOwner(owner);
  };

  const handleClosePrModal = () => {
    //This needs to be the end all be all close for all PR modals. Each sequential modal should just invoke this guy.
    setOpenCreatePaymentRequestModal(false);
    setSelectedOwner({} as OwnerV2Dto);
    dispatch({
      type: getResetPrState.type,
    });
    dispatch({
      type: resetQuickPayState.type,
    });
    dispatch({
      type: getCloseAllModals.type,
    });
  };

  const updateCurrentPage = (newValue: number) => {
    setCurrentPage(newValue);
  };

  const handleSort = (column: OwnerSortColumnEnum) => {
    if (selectedSortingColumn === column) {
      setSelectedSortingDirection((prevDirection: string) => (prevDirection === OwnerSortDirectionEnum.Asc ? OwnerSortDirectionEnum.Desc : OwnerSortDirectionEnum.Asc));
    } else {
      setSelectedSortingColumn(column);
      setSelectedSortingDirection(OwnerSortDirectionEnum.Asc);
    }
    setRecordStartIndex(0);
    setCurrentPage(0);
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', pb: '64px', width: '100%', position: 'relative' }}>
      {/* This is an overlay for marketing purposes when this product is not yet purchased or configured */}
      {isFetchingPmDetailsSuccess && !isOwnerPortalFeatureEnabled && (
        <Box
          sx={{
            position: 'absolute',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0.95) 50%, rgba(255, 255, 255, 0.13) 70%);',
            height: '100%',
            width: '100%',
            left: 0,
            top: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <Box sx={{ position: 'absolute', bottom: '15%', width: '50%' }}>
            <Typography style={{ textAlign: 'center', marginBottom: 20, fontFamily: 'Work Sans' }}>
              Welcome to the Owner's Section. Your account is currently not configured to use this feature. Please contact our sales team and we'll be happy to get you situated.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                sx={outlinedButtonStyle}
                onClick={() => {
                  window.open(`https://calendly.com/inosio/meet-with-inosio-sales`);
                }}
              >
                Contact Sales
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
        <OwnersFilterTableHeader
          rangeSize={rangeSize}
          startIndex={recordStartIndex}
          updateCurrentPage={updateCurrentPage}
          sortColumn={selectedSortingColumn}
          sortDirection={selectedSortingDirection}
          handleSearch={handleSearch2}
        />
      </Box>
      {isFetchingOwnersFailed && String(errorMessage.statusCode)[0] === '4' ? (
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
          <Grid item display="flex" flexDirection="column" alignItems="center">
            <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
              <img src={BadRequestIcon} alt="400" />
            </Grid>
            <Grid item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '23px',
                  color: '#2E82AF',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.statusCode}
              </Typography>
            </Grid>
            <Grid mt={2} item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                Page not found.
              </Typography>
            </Grid>
            <Grid item justifyContent="center" mt={2} xs={8}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.message.length === 0 ? '' : errorMessage.message}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : isFetchingOwnersFailed && String(errorMessage.statusCode)[0] === '5' ? (
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
          <Grid item display="flex" flexDirection="column" alignItems="center">
            <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
              <img src={Error500} alt="500" style={{ height: '35vh' }} />
            </Grid>
            <Grid item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '40px',
                  lineHeight: '23px',
                  color: '#2E82AF',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.statusCode}
              </Typography>
            </Grid>
            <Grid mt={2} item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                Page not found.
              </Typography>
            </Grid>
            <Grid item justifyContent="center" mt={2} xs={8}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {errorMessage.message.length === 0 ? '' : errorMessage.message}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : isFetchingOwners ? (
        <Stack justifyContent="center" alignItems="center" style={{ height: '1100px' }}>
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : (
        <TableContainer style={{ flex: 1 }}>
          <Divider sx={{ marginTop: 2 }} />
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TableSortLabel
                      active={selectedSortingColumn === OwnerSortColumnEnum.Name}
                      direction={selectedSortingDirection === OwnerSortDirectionEnum.Asc ? 'asc' : 'desc'}
                      onClick={() => handleSort(OwnerSortColumnEnum.Name)}
                    >
                      Name
                    </TableSortLabel>
                  </Stack>
                </TableCell>
                <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TableSortLabel
                      active={selectedSortingColumn === OwnerSortColumnEnum.OwnerId}
                      direction={selectedSortingDirection === OwnerSortDirectionEnum.Asc ? 'asc' : 'desc'}
                      onClick={() => handleSort(OwnerSortColumnEnum.OwnerId)}
                    >
                      Owner ID
                    </TableSortLabel>
                  </Stack>
                </TableCell>
                <TableCell id="subTitle" sx={{ cursor: 'pointer' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TableSortLabel
                      active={selectedSortingColumn === OwnerSortColumnEnum.Property}
                      direction={selectedSortingDirection === OwnerSortDirectionEnum.Asc ? 'asc' : 'desc'}
                      onClick={() => handleSort(OwnerSortColumnEnum.Property)}
                    >
                      Property
                    </TableSortLabel>
                  </Stack>
                </TableCell>
                <TableCell id="subTitle">
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    Details
                  </Stack>
                </TableCell>
                <TableCell id="subTitle">
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    Registration
                  </Stack>
                </TableCell>
                <TableCell id="subTitle">
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    Request a Payment
                  </Stack>
                </TableCell>
                <TableCell id="subTitle">
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    Owner Portal
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{ left: '28px' }}>
              {tableData?.map((eachRow) => {
                const isAtLeastOneRegisteredUser = !!eachRow.UserProfiles?.length;
                return (
                  <TableRow key={eachRow.Uuid}>
                    <TableCell id="content">{`${eachRow.FirstName}${eachRow.MiddleName ? ' '.concat(eachRow.MiddleName) : ''} ${eachRow.LastName}`}</TableCell>
                    <TableCell id="content">{eachRow.ExternalRefId2}</TableCell>
                    <TableCell id="content">{eachRow.Properties?.length > 1 ? '2+ Properties' : eachRow.Properties[0]?.Name || ''}</TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <img width={13} height={14} src={ReviewOtpIcon} alt="details" style={{ cursor: 'pointer' }} onClick={() => handleViewOwnerDetails(eachRow)} />
                      </Stack>
                    </TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <Tooltip
                          title={isAtLeastOneRegisteredUser ? 'Reset Password' : 'Invite to Portal'}
                          placement="right"
                          onClick={() => {
                            handleOpenModal(eachRow); //owner
                            setOpenUserRegistrationModalManager(true);
                          }}
                        >
                          <img
                            src={isAtLeastOneRegisteredUser ? RegisteredIcon : NotRegisteredIcon}
                            alt=""
                            style={{ cursor: 'pointer' }}
                            className={isAtLeastOneRegisteredUser ? 'registered' : 'not-registered'}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <img
                          onClick={() => {
                            setSelectedOwner(eachRow);
                            setOpenCreatePaymentRequestModal(true);
                          }}
                          src={PaymentRequestIcon}
                          alt="Request a payment"
                          style={{ cursor: 'pointer' }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell id="content">
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <Tooltip title="Login to owner portal to make payment and perform other functions." placement="right">
                          {/* this will log pm into owner portal */}
                          <img
                            id="new-tab"
                            src={ResidentPortalIcon}
                            alt=""
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedOwner(eachRow);
                              if (eachRow.UserProfiles?.length === 1) {
                                const userToImpersonate = eachRow.UserProfiles?.[0]?.UserUuid;
                                window.open(`/login?logAsEntityUuid=${eachRow.Uuid}&logAsUserUuid=${userToImpersonate}`, '_blank');
                              } else if (!eachRow.UserProfiles?.length) {
                                window.open(`/login?logAsEntityUuid=${eachRow.Uuid}`, '_blank');
                              } else {
                                setOpenLogAsProfileUsersModal(true);
                              }
                            }}
                            className="logas-res-btn"
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        rowsPerPageOptions={[50, 100, 200]}
        count={OwnersData?.TotalCount}
        page={currentPage}
        onPageChange={handleChangePage}
        rowsPerPage={rangeSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ position: 'absolute', bottom: 10, right: 10, zIndex: 0 }}
      />
      <UserRegistrationModalManager
        profileUsers={selectedOwner?.UserProfiles || []}
        profileInvitedUsers={selectedOwner?.InvitedProfiles || []}
        onClose={() => {
          setSelectedOwner({} as OwnerV2Dto);
          setOpenUserRegistrationModalManager(false);
        }}
        profileDetails={
          {
            profileUuid: selectedOwner?.Uuid,
            profileRole: Role.Owner,
            profileIntegrationEmail: selectedOwner?.IntegrationEmail,
            profileIntegrationFirstName: selectedOwner?.FirstName,
            profileIntegrationLastName: selectedOwner?.LastName,
            profileIntegrationMiddleName: selectedOwner?.MiddleName,
          } as UserRegistrationProfileDetails
        }
        open={openUserRegistrationModalManager}
      />
      <LogAsProfileUserModal
        open={openLogAsProfileUsersModal}
        onClose={() => {
          setOpenLogAsProfileUsersModal(false);
          setSelectedOwner({} as OwnerV2Dto);
        }}
        profileUsers={selectedOwner?.UserProfiles || []}
        profileUuid={selectedOwner?.Uuid || ''}
      />
      {selectedOwner && <OwnerCreatePaymentRequestModal ownerDto={selectedOwner} open={openCreatePaymentRequestModal} onClose={handleClosePrModal} />}
    </Box>
  );
}
